/**
 * @fileoverview Doctor Dashboard Page
 * @description Main dashboard view for doctor role showing KPIs and upcoming assignments
 * @author MCHD Room Scheduler Team
 * @created 07-10-2025
 * @complies @ui-ux-excellence.mdc - Hospital-specific design for medical professionals
 */

import { Container, Title, Text, Stack, Alert, Button } from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { useDoctorDashboard, extractDashboardKPIs, getDashboardError } from '../../hooks/doctor/useDoctorDashboard';
import { DoctorKPICards, DoctorKPICardsSkeleton } from '../../features/doctor/dashboard/DoctorKPICards';
import { UpcomingAssignmentsList } from '../../features/doctor/dashboard/UpcomingAssignmentsList';

/**
 * Doctor Dashboard Page Component
 * @description Landing page for doctors showing personal schedule overview
 * 
 * @example
 * Route: /doctor/dashboard
 * Access: Doctors only (requireDoctor middleware)
 * 
 * Features:
 * - Dashboard KPIs (upcoming sessions, buildings, rooms, weekly breakdown)
 * - Upcoming assignments list with filters
 * - Real-time data updates every 5 minutes
 * - Loading states and error handling
 */
export default function DoctorDashboard() {
  // Fetch dashboard data
  const dashboardQuery = useDoctorDashboard();
  const kpis = extractDashboardKPIs(dashboardQuery);
  const error = getDashboardError(dashboardQuery);

  /**
   * Handle manual refresh
   */
  const handleRefresh = () => {
    dashboardQuery.refetch();
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Page Header */}
        <div>
          <Title order={1} mb="xs">
            My Dashboard
          </Title>
          <Text c="dimmed" size="sm">
            Overview of your upcoming sessions and assignments
          </Text>
        </div>

        {/* Error State */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error Loading Dashboard"
            color="red"
            variant="light"
            withCloseButton
            onClose={handleRefresh}
          >
            <Text size="sm" mb="sm">
              {error}
            </Text>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={handleRefresh}
              loading={dashboardQuery.isRefetching}
            >
              Retry
            </Button>
          </Alert>
        )}

        {/* KPI Cards */}
        {dashboardQuery.isLoading ? (
          <DoctorKPICardsSkeleton />
        ) : kpis ? (
          <DoctorKPICards kpis={kpis} />
        ) : null}

        {/* Upcoming Assignments List */}
        <UpcomingAssignmentsList maxHeight={500} />

        {/* Last Updated Timestamp */}
        {dashboardQuery.dataUpdatedAt > 0 && (
          <Text size="xs" c="dimmed" ta="right">
            Last updated: {new Date(dashboardQuery.dataUpdatedAt).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        )}
      </Stack>
    </Container>
  );
}

